import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { formatAmount } from "./format";
import { STATUS_GOOD, STATUS_CRITICAL, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_MUTED } from "./palette";

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75em;
`;

const Tile = styled.div`
  flex: 1 1 240px;
  padding: 1.1em 1.4em;
  background: #fff;
  border: 1px solid rgba(11, 11, 11, 0.06);
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const CurrencyName = styled.div`
  font-size: 12px;
  color: ${TEXT_SECONDARY};
  margin-bottom: 0.45em;
`;

const NetLabel = styled.div`
  font-size: 12px;
  color: ${TEXT_SECONDARY};
`;

const NetValue = styled.div`
  font-size: 26px;
  line-height: 1.2;
  font-weight: 600;
  color: ${(props) => props.$color};
`;

const Breakdown = styled.div`
  display: flex;
  gap: 1.25em;
  margin-top: 0.6em;
  font-size: 13px;
`;

const Income = styled.span`
  color: ${STATUS_GOOD};
`;

const Expense = styled.span`
  color: ${STATUS_CRITICAL};
`;

const Delta = styled.div`
  margin-top: 0.6em;
  font-size: 12px;
  color: ${(props) => (props.$isNeutral ? TEXT_MUTED : props.$color)};
  display: flex;
  align-items: center;
  gap: 0.3em;
`;

function formatPercent(delta, base) {
  if (base <= 0) {
    return null;
  }

  const percent = (Math.abs(delta) / base) * 100;
  // 小於 10% 保留一位小數，避免 0.4% 被顯示成誤導的「0%」
  return percent < 10 ? percent.toFixed(1) : String(Math.round(percent));
}

function CurrencyTile({
  currency,
  currentIncome,
  currentExpense,
  previousIncome,
  previousExpense,
}) {
  const net = currentIncome - currentExpense;
  const previousNet = previousIncome - previousExpense;
  const delta = net - previousNet;
  const isNeutral = delta === 0;
  const isIncrease = delta > 0;
  // 結餘增加視為「理想」，用 good 色；減少視為「不理想」，用 critical 色。
  const deltaColor = isIncrease ? STATUS_GOOD : STATUS_CRITICAL;
  const netColor = net >= 0 ? TEXT_PRIMARY : STATUS_CRITICAL;

  let deltaText;
  if (isNeutral) {
    deltaText = "與上月持平";
  } else {
    const percent = formatPercent(delta, Math.abs(previousNet));
    deltaText = `${formatAmount(Math.abs(delta))}${
      percent !== null ? ` (${percent}%)` : ""
    } 相較上月`;
  }

  return (
    <Tile>
      <CurrencyName>{currency.name}</CurrencyName>
      <NetLabel>本月結餘</NetLabel>
      <NetValue $color={netColor}>{formatAmount(net)}</NetValue>
      <Breakdown>
        <Income>{`收入 ${formatAmount(currentIncome)}`}</Income>
        <Expense>{`支出 ${formatAmount(currentExpense)}`}</Expense>
      </Breakdown>
      <Delta $color={deltaColor} $isNeutral={isNeutral}>
        {!isNeutral && (isIncrease ? <ArrowUpOutlined /> : <ArrowDownOutlined />)}
        {deltaText}
      </Delta>
    </Tile>
  );
}

CurrencyTile.propTypes = {
  currency: PropTypes.shape({ name: PropTypes.string.isRequired }).isRequired,
  currentIncome: PropTypes.number.isRequired,
  currentExpense: PropTypes.number.isRequired,
  previousIncome: PropTypes.number.isRequired,
  previousExpense: PropTypes.number.isRequired,
};

export default function CurrencySummaryCards({ currencySummary }) {
  if (!currencySummary || currencySummary.length === 0) {
    return null;
  }

  return (
    <Row>
      {currencySummary.map((summary) => (
        <CurrencyTile
          key={summary.currency.id}
          currency={summary.currency}
          currentIncome={summary.currentIncome}
          currentExpense={summary.currentExpense}
          previousIncome={summary.previousIncome}
          previousExpense={summary.previousExpense}
        />
      ))}
    </Row>
  );
}

CurrencySummaryCards.propTypes = {
  currencySummary: PropTypes.arrayOf(
    PropTypes.shape({
      currency: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }).isRequired,
      currentIncome: PropTypes.number.isRequired,
      currentExpense: PropTypes.number.isRequired,
      previousIncome: PropTypes.number.isRequired,
      previousExpense: PropTypes.number.isRequired,
    })
  ),
};

CurrencySummaryCards.defaultProps = {
  currencySummary: null,
};
